import React from 'react';
import { Link } from 'react-router-dom';
import { Server, CheckCircle, Cpu, HardDrive, Zap, Shield, ArrowRight, Phone, Database } from 'lucide-react';

export default function GestionDatos() {
  const caracteristicas = [
    {
      icon: Cpu,
      titulo: 'Cómputo de Alto Rendimiento',
      descripcion: 'Servidores HPE ProLiant con procesadores Intel Xeon y AMD EPYC de última generación.'
    },
    {
      icon: HardDrive,
      titulo: 'Almacenamiento Inteligente',
      descripcion: 'HPE Alletra Storage con IA integrada para optimización automática y predicción de fallos.'
    },
    {
      icon: Shield,
      titulo: 'Protección de Datos 24/7',
      descripcion: 'HPE Zerto para replicación continua y Cyber Resilience Vault para protección contra ransomware.'
    },
    {
      icon: Database,
      titulo: 'Data Services Avanzados',
      descripcion: 'Data Fabric y servicios de gestión para optimizar el ciclo de vida de tus datos.'
    }
  ];

  const soluciones = [
    {
      categoria: 'Cómputo Empresarial',
      tipo: 'HPE ProLiant Servers',
      descripcion: 'Servidores de alto rendimiento con seguridad integrada desde el firmware',
      casos: 'Virtualización, bases de datos, aplicaciones empresariales, IA/ML',
      productos: ['ProLiant DL Gen11', 'ProLiant ML Gen11', 'ProLiant BL Gen11', 'Apollo Systems']
    },
    {
      categoria: 'Almacenamiento Flash',
      tipo: 'HPE Alletra Storage MP',
      descripcion: 'Almacenamiento all-flash con IA para optimización automática y predicción de fallos',
      casos: 'Bases de datos críticas, virtualización, contenedores, analytics',
      productos: ['Alletra Storage MP', 'Alletra 9000', 'Alletra 6000', 'Alletra 4000']
    },
    {
      categoria: 'Protección de Datos',
      tipo: 'HPE Data Protection',
      descripcion: 'Respaldo, replicación y recuperación ante desastres con RPO cercano a cero',
      casos: 'Backup continuo, DR, protección contra ransomware, compliance',
      productos: ['HPE Zerto', 'Cyber Resilience Vault', 'StoreOnce', 'Data Protection Services']
    },
    {
      categoria: 'Gestión de Datos',
      tipo: 'HPE Data Services',
      descripcion: 'Orquestación y optimización del ciclo de vida de datos en ambientes híbridos',
      casos: 'Data governance, compliance, optimización multi-cloud, data fabric',
      productos: ['Data Fabric', 'Data Services Cloud Console', 'InfoSight', 'Cloud Data Services']
    }
  ];

  const beneficios = [
    'Hasta 70% reducción de latencia con almacenamiento all-flash',
    'Disponibilidad garantizada del 99.9999% (6 nueves)',
    'Protección automática contra ransomware con Cyber Resilience Vault',
    'Reducción de costos operativos del 40%',
    'Predicción proactiva de fallos con IA integrada',
    'RPO cercano a cero con replicación continua HPE Zerto',
    'Gestión unificada de datos en ambientes híbridos'
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=2134)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/90 via-gray-900/85 to-teal-900/90"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-block bg-teal-600/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <span className="text-teal-400 font-semibold">Infraestructura de Cómputo y Almacenamiento</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Gestión de Datos
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8">
              Cómputo, almacenamiento y protección integral de datos empresariales con HPE
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/contacto" 
                className="bg-teal-600 text-white px-8 py-4 rounded-lg hover:bg-teal-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
              >
                Solicitar Asesoría <ArrowRight className="ml-2" />
              </Link>
              <a 
                href="#soluciones" 
                className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg hover:bg-white/20 transition-colors text-lg font-semibold inline-flex items-center justify-center border border-white/30"
              >
                Ver Soluciones
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Introducción */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Infraestructura completa para tus datos
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                La <strong>gestión de datos</strong> va más allá del almacenamiento. Incluye cómputo, 
                almacenamiento, protección y servicios que aseguran que tus datos estén disponibles, 
                protegidos y optimizados. En ALLTI implementamos soluciones completas con tecnología 
                <strong> HPE</strong>: desde servidores ProLiant hasta sistemas Alletra, Zerto y Data Services.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                Ofrecemos una arquitectura integrada donde el cómputo (servidores) y el almacenamiento 
                trabajan en conjunto con inteligencia artificial para optimización automática, protección 
                continua contra ransomware, y gestión centralizada de todo el ciclo de vida de datos.
              </p>
              <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg">
                <p className="text-gray-800 font-semibold mb-2">
                  <HardDrive className="inline mr-2 text-teal-600" size={24} />
                  Solución Integral
                </p>
                <p className="text-gray-600">
                  La combinación de HPE ProLiant (cómputo seguro) con HPE Alletra (almacenamiento 
                  inteligente) y HPE Zerto (protección continua) crea una infraestructura de datos 
                  resiliente, segura y de alto rendimiento.
                </p>
              </div>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1544197150-b99a580bb7a8?q=80&w=2070" 
                alt="Data Infrastructure"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Características */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Componentes de la Solución
            </h2>
            <p className="text-xl text-gray-600">
              Una plataforma completa para datos empresariales
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {caracteristicas.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-2xl transition-shadow">
                  <div className="bg-teal-50 w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                    <Icon className="text-teal-600" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.titulo}</h3>
                  <p className="text-gray-600">{item.descripcion}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Portafolio de Soluciones */}
      <section id="soluciones" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Portafolio de Soluciones
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cómputo, almacenamiento y protección integral con tecnología HPE
            </p>
          </div>
          <div className="space-y-8">
            {soluciones.map((solucion, index) => (
              <div 
                key={index}
                className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all"
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="bg-teal-50 p-3 rounded-lg">
                        <HardDrive className="text-teal-600" size={28} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{solucion.categoria}</h3>
                        <p className="text-allti-blue font-semibold">{solucion.tipo}</p>
                      </div>
                    </div>
                    <p className="text-gray-600 mb-4 text-lg">{solucion.descripcion}</p>
                    <div className="mb-4">
                      <span className="text-sm font-semibold text-gray-700">Casos de uso ideales:</span>
                      <p className="text-gray-600">{solucion.casos}</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Productos:</h4>
                    <ul className="space-y-2">
                      {solucion.productos.map((producto, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-gray-600">
                          <CheckCircle className="text-hpe-green" size={16} />
                          <span>{producto}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <h2 className="text-4xl font-bold mb-6">
                Resultados Comprobados
              </h2>
              <p className="text-xl text-teal-100 mb-8">
                Organizaciones que implementan soluciones integrales de gestión de datos con HPE experimentan:
              </p>
              <ul className="space-y-4">
                {beneficios.map((beneficio, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-hpe-green flex-shrink-0 mt-1" size={24} />
                    <span className="text-lg text-teal-50">{beneficio}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <img 
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070" 
                alt="Performance Dashboard"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Gestión Inteligente */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=2070" 
                alt="Intelligent Management"
                className="rounded-xl shadow-2xl"
              />
            </div>
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Gestión Inteligente con IA
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Toda la infraestructura se gestiona de forma centralizada con inteligencia artificial 
                que optimiza recursos, predice fallos y automatiza operaciones rutinarias.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-4">
                  <div className="bg-allti-blue/10 p-3 rounded-lg">
                    <Zap className="text-allti-blue" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">HPE InfoSight</h3>
                    <p className="text-gray-600">IA que analiza millones de sensores para prevenir problemas</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-allti-blue/10 p-3 rounded-lg">
                    <Shield className="text-allti-blue" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Data Services Cloud Console</h3>
                    <p className="text-gray-600">Portal unificado para toda tu infraestructura de datos</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="bg-allti-blue/10 p-3 rounded-lg">
                    <Server className="text-allti-blue" size={24} />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Automatización Total</h3>
                    <p className="text-gray-600">Provisioning, backup y recuperación automatizados</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <HardDrive className="text-teal-600 mx-auto mb-6" size={64} />
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Transforma tu gestión de datos
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Como partner certificado de HPE, ALLTI ofrece diseño, implementación y soporte 
            especializado para infraestructura completa de datos: cómputo, almacenamiento y protección.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contacto" 
              className="bg-teal-600 text-white px-8 py-4 rounded-lg hover:bg-teal-700 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              <Phone className="mr-2" size={20} />
              Contactar a un Experto
            </Link>
            <Link 
              to="/casos-de-uso" 
              className="bg-gray-100 text-gray-900 px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors text-lg font-semibold inline-flex items-center justify-center"
            >
              Ver Casos de Éxito
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
